<script setup lang="ts">
import type { IconName } from '~ds/lib/icons'
import type { HTMLAttributes } from 'vue'

import { IconGlyph } from '#components'
import { cn } from '~ds/lib/utils'
import { computed } from 'vue'

import type { IconVariants } from '.'

import { iconVariants } from '.'

const props = defineProps<{
  /** A glyph from the ICONS registry. Omit it to slot an inline SVG instead. */
  name?: IconName;
  /**
   * Without it a named glyph carries no `size-*` class, so a parent that sizes its svgs
   * (`[&_svg:not([class*='size-'])]:size-4` on Button, menu items, …) decides; standalone it is 24px.
   */
  size?: IconVariants['size'];
  label?: string;
  class?: HTMLAttributes['class'];
}>()

// The off-grid `custom:` collection says so, and the recipe's low-DPI stroke compensation leaves it alone.
const collection = computed(() => (props.name?.startsWith('custom:') ? 'custom' : undefined))

// Iconify hides its svg from assistive tech unless told otherwise, and drops the attribute on 'false'.
const ariaHidden = computed(() => (props.label ? 'false' : 'true'))
</script>

<template>
  <IconGlyph
    v-if="name"
    data-slot="icon"
    :name="name"
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="ariaHidden"
    :data-collection="collection"
    :class="cn(iconVariants({ size }), props.class)"
  />
  <span
    v-else
    data-slot="icon"
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : true"
    data-collection="custom"
    :class="cn('inline-flex size-5 shrink-0 items-center justify-center [&>svg]:size-full [&>svg]:fill-current', iconVariants({ size }), props.class)"
  >
    <slot />
  </span>
</template>
